import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import {
  Package, Heart, MapPin, ChevronRight, Settings, Gift, Wallet, Star, ShoppingBag, RotateCcw, Clock,
} from 'lucide-react';

export default function ShopAccountPage() {
  const { user } = useOutletContext();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }} data-testid="account-not-logged-in" className="shop-page">
        <ShoppingBag className="w-12 h-12 mx-auto text-orange-400 mb-3" />
        <h2 className="text-lg font-bold mb-1">Login to view your account</h2>
        <p className="text-sm text-gray-500 mb-4">Track orders, manage your wallet & more</p>
        <Link to="/auth" className="inline-block px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg" data-testid="account-login-cta">
          Login
        </Link>
      </div>
    );
  }

  const stats = [
    { id: 'orders', icon: Package, label: 'Orders', value: 0, href: '/shop/orders', color: 'orange' },
    { id: 'wishlist', icon: Heart, label: 'Wishlist', value: (() => {
      try { return JSON.parse(localStorage.getItem('dharmshop_wishlist') || '[]').length; } catch { return 0; }
    })(), href: '/shop/wishlist', color: 'rose' },
    { id: 'wallet', icon: Wallet, label: 'Wallet', value: '₹0', href: '/shop/wallet', color: 'green' },
  ];

  const quickLinks = [
    { id: 'recent', icon: Clock, label: 'Recently Viewed', href: '/shop' },
    { id: 'returns', icon: RotateCcw, label: 'Returns & Refunds', href: '/shop/orders' },
    { id: 'addresses', icon: MapPin, label: 'Saved Addresses', href: '/shop/addresses' },
    { id: 'rewards', icon: Gift, label: 'Rewards', href: '/shop/rewards' },
    { id: 'rate', icon: Star, label: 'Rate Us', href: '#' },
    { id: 'settings', icon: Settings, label: 'Settings', href: '/settings' },
  ];

  return (
    <div className="shop-page" data-testid="account-page" style={{ paddingBottom: 100 }}>
      {/* Header */}
      <div className="shop-fk-section" style={{ marginTop: 8 }}>
        <div className="flex items-center gap-3">
          {user.avatar && !user.avatar.includes('ui-avatars.com') ? (
            <img src={user.avatar} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-orange-100" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center text-xl font-bold text-orange-700">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-gray-900 truncate">{user.name}</div>
            <div className="text-xs text-gray-500 truncate">{user.email}</div>
            <button
              onClick={() => navigate('/profile')}
              className="text-[11px] text-[#FF7A00] font-bold mt-0.5"
              data-testid="account-edit-profile"
            >
              Edit Profile →
            </button>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="shop-fk-section">
        <div className="grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <Link
              key={s.id}
              to={s.href}
              className="flex flex-col items-center p-3 bg-gray-50 rounded-lg"
              data-testid={`account-stat-${s.id}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1
                ${s.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                  s.color === 'rose' ? 'bg-rose-100 text-rose-600' :
                  'bg-green-100 text-green-600'}
              `}>
                <s.icon className="w-4 h-4" />
              </div>
              <div className="text-base font-bold text-gray-900">{s.value}</div>
              <div className="text-[11px] text-gray-500 font-semibold">{s.label}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick links grid */}
      <div className="shop-fk-section">
        <h3 className="shop-fk-section-title">Quick Actions</h3>
        <div className="grid grid-cols-3 gap-3">
          {quickLinks.map((q) => (
            <Link
              key={q.id}
              to={q.href}
              className="flex flex-col items-center gap-1.5 p-4 bg-white border border-gray-100 rounded-lg hover:border-orange-200 hover:bg-orange-50/40 transition-colors"
              data-testid={`account-quick-${q.id}`}
            >
              <q.icon className="w-5 h-5 text-orange-600" />
              <span className="text-[11px] font-semibold text-gray-800 text-center">{q.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Footer link */}
      <div className="shop-fk-section">
        <Link to="/shop/menu" className="flex items-center justify-between py-2 text-sm text-gray-700 font-semibold">
          More options & support
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </Link>
      </div>
    </div>
  );
}
